/**
 * `@Name` tokens in a comment body. The body is plain text, so a mention is kept
 * only while its exact token still stands on its own in the text, and every member
 * gets a token no other member shares.
 */

export interface MentionCandidate { id: string; name: string; handle?: string | null }

const key = (value: string) => value.trim().toLocaleLowerCase();

/**
 * One label per member. A name shared by two or more members becomes
 * `Name (@handle)`, or `Name (2)` when the handle is missing or also shared,
 * numbered in id order so every client derives the same labels.
 */
export function mentionLabels(members: MentionCandidate[]): Map<string, string> {
  const byName = new Map<string, MentionCandidate[]>();
  for (const member of members) {
    const group = byName.get(key(member.name)) ?? [];
    group.push(member);
    byName.set(key(member.name), group);
  }
  const labels = new Map<string, string>();
  for (const group of byName.values()) {
    if (group.length === 1) { labels.set(group[0].id, group[0].name.trim()); continue; }
    const handles = new Map<string, number>();
    for (const member of group) if (member.handle) handles.set(key(member.handle), (handles.get(key(member.handle)) ?? 0) + 1);
    [...group].sort((a, b) => a.id.localeCompare(b.id)).forEach((member, index) => {
      const handle = member.handle && handles.get(key(member.handle)) === 1 ? member.handle : null;
      const name = member.name.trim();
      labels.set(member.id, handle ? `${name} (@${handle.replace(/^@/, '')})` : `${name} (${index + 1})`);
    });
  }
  return labels;
}

const escape = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * True when `@label` appears as its own token: not glued to a preceding word
 * (`a@Sam`, an email) and not the start of a longer name (`@Samantha`).
 */
export function hasMention(text: string, label: string): boolean {
  if (!label) return false;
  return new RegExp(`(?:^|[^\\p{L}\\p{N}_@])@${escape(label)}(?![\\p{L}\\p{N}_])`, 'u').test(text);
}
